/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {afterNextRender, Component, computed, HostListener, OnInit, signal} from '@angular/core';
import {EditableGridComponent} from '../editable-grid-component';
import {CellEditingStoppedEvent, ColDef, RowClassRules} from 'ag-grid-community';
import {RestrictedMomentumProgram} from '../../model/restrictedMomentumProgram.model';
import {
    CustomMultiSelectFilterComponent
} from '../filter-components/custom-multi-select-filter/custom-multi-select-filter.component';
import {
    CustomMultiSelectFloatingFilterComponent
} from '../filter-components/custom-multi-select-filter/custom-multi-select-floating-filter.component';
import {MomentumProgramAccess} from '../../model/momentumProgramAccess';
import {ModalCellEditorComponent} from '../cell-renderer/modal-cell-editor.component';
import {ActionsCellRendererComponent} from '../cell-renderer/actions-cell-renderer.component';
import {SearchUser} from '../../shared/model/user';
import {refreshCellAndDeferredRowRedraw} from '../cell-renderer/prime-cell-editor-tab.util';

@Component({
    selector: 'app-restricted-programs-component',
    templateUrl: './restricted-programs.component.html',
    styleUrls: ['./restricted-programs.component.scss'],
    standalone: false
})
export class RestrictedProgramsComponent extends EditableGridComponent implements OnInit {
  permissionUrl = '/esciUI/restrictedMomentumProgramPermission';
  saveUrl = '/esciUI/saveRestrictedMomentumProgramAccess';
  restrictedPrograms: RestrictedMomentumProgram[];
  readonly canViewPage = computed(() => this.permission());
  readonly isModalVisible = signal(false);
  readonly showModalContent = computed(() => this.isModalVisible());
  private readonly modalUserSignal = signal<SearchUser | null>(null);
  private readonly modalUserNotNewSignal = signal(false);
  readonly canAddUserInModal = computed(() => !this.modalUserNotNewSignal() && !!this.modalUserSignal());
  rowClassRules: RowClassRules = {
    'modified-row': params => params.data.dataChanged
  };
  cols: ColDef[] = [
    {field: 'momentumProgram.momentumId', headerName: 'Momentum ID', editable: false, hide: true},
    {field: 'momentumProgram.modelYear', maxWidth: 160, headerName: 'Model Year', editable: false, filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent},
    {field: 'momentumProgram.programName', maxWidth: 250, headerName: 'Program Name', editable: false, filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent},
    {field: 'momentumProgram.projectName', maxWidth: 400, headerName: 'Project Name', editable: false, filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent},
    {field: 'users', headerName: 'Allowed Users', editable: false, minWidth: 300,
      valueGetter: (params) => this.getValueFromUsers(params.data['users']),
      filterValueGetter: (params) => this.getValueFromUsers(params.data['users'])},
    {headerName: 'Change Users', colId: 'actions', editable: false, filter: false,
      minWidth: 130, maxWidth: 130,
      cellRenderer: ModalCellEditorComponent,
      cellRendererParams: {
        openModal: this.openModal.bind(this),
        label: 'Edit Users'
      }
      },
  ];

  ngOnInit(): void {
    this.permission.set(false);
    this.loading = true;
    this.esciService.getPermissionAndFileSizeLimit(this, false);
    this.esciService.getRestrictedMomentumProgramList(this, () => this.reset());
  }

  saveRestrictedPrograms() {
    const valid = this.areRowsValid(this.restrictedPrograms);
    if (valid && this.adminPermission()) {
      this.saving = true;
      // convert restrictedPrograms grid data back into a list of programAccess objects
      const programAccessList = [];
        this.restrictedPrograms.forEach(restrictedProgram => {
            restrictedProgram.users.forEach(user => {
            const programAccess = new MomentumProgramAccess();
            programAccess.gmin = user.gmin;
            programAccess.momentumId = restrictedProgram.momentumProgram.momentumId;
            programAccessList.push(programAccess);
            });
        });
      // clear out dataChanged members
      this.dataChanged = false;
      for (const row of this.restrictedPrograms) {
        row['dataChanged'] = false;
      }
      this.esciService.saveRows(this, programAccessList, 'Restricted Program',() => this.reset());
    }
  }

  exportReport() {
    // export all data in the grid
    this.excelService.exportGridDataAsExcelFile(this.gridApi, 'Restricted_Program_Report');
  }

  resetChanges() {
    this.resetting = true;
    this.exporting = true;
    this.messageService.clear();
    this.esciService.getRestrictedMomentumProgramList(this, () => this.reset());
  }

  reset() {
    super.reset();
    this.refreshGrid();
  }

  rowIndexOfProgram: any;
  modalRowData = [];
  protected modalDataChanged: boolean;
  modalUser: SearchUser;
  modalUserNotNew = false;
  modalColumnDefs = [
    { headerName: 'Delete',
      cellRenderer: ActionsCellRendererComponent,
      minWidth: 130, maxWidth: 130, editable: false,
      cellRendererParams: {
        onDeleteClick: this.onDeleteClick.bind(this),
        hideCopyButton: true,
      }},
    { headerName: 'Full Name', field: 'fullName', editable: false },
    { headerName: 'GMIN', field: 'gmin', editable: false },
  ];

  openModal(params: any) {
    this.isModalVisible.set(true);
    this.modalDataChanged = params.data.dataChanged;
    this.modalRowData = params.data.users;
    this.rowIndexOfProgram = params.rowIndex;
  }

  canSaveAllChanges(): boolean {
    return !!this.dataChanged && !this.duplicate && !this.rowsInvalid;
  }

  onDialogVisibilityChange(visible: boolean): void {
    this.isModalVisible.set(!!visible);
    if (!visible) {
      this.checkModalDataChanged();
      this.refreshGrid();
      this.modalUser = null;
      this.modalUserNotNew = false;
      this.modalUserSignal.set(null);
      this.modalUserNotNewSignal.set(false);
    }
  }

  onDeleteClick(params): void {
    // find the index of the row clicked on
    const index = this.modalRowData.findIndex(row => row.gmin === params.data.gmin);
    this.modalRowData.splice(index, 1);
    this.modalDataChanged = true;
    // set this.dataChanged based on if any row in row has dataChanged
    this.checkModalDataChanged();
    afterNextRender(() => {
      if (this.modalGridApi && !this.modalGridApi.isDestroyed()) {
        this.modalGridApi.setGridOption('rowData', this.modalRowData);
        this.modalGridApi.sizeColumnsToFit();
      }
    }, { injector: this.injector });
  }
  checkModalDataChanged() {
    this.modalDataChanged =  this.modalDataChanged || this.modalRowData.some(row => row['dataChanged']);
    this.dataChanged = this.modalDataChanged || this.dataChanged;
    // set the dataChanged flag on the row with index rowIndexOfProgram
    this.restrictedPrograms.find((row, index) => {
        if (index === this.rowIndexOfProgram) {
            row['dataChanged'] = this.modalDataChanged;
            return true;
        }
    });
  }

  modalUserSelected(user: any) {
    // check if user already exists in row data
    this.modalUserNotNew  = this.modalRowData.some(row => row.gmin === user.gmin);
    this.modalUser = user;
    this.modalUserNotNewSignal.set(this.modalUserNotNew);
    this.modalUserSignal.set(this.modalUser || null);
  }
  addUserInModal() {
    if (this.modalUserNotNew) {
      return;
    }
    this.modalRowData.push(this.modalUser);
    this.modalDataChanged = true;
    // set this.dataChanged based on if any row in row has dataChanged
    this.modalDataChanged =  this.modalDataChanged || this.modalRowData.some(row => row['dataChanged']);
    this.dataChanged = this.modalDataChanged || this.dataChanged;
    this.modalGridApi.setGridOption('rowData', this.modalRowData);
    afterNextRender(() => {
      if (this.modalGridApi && !this.modalGridApi.isDestroyed()) {
        this.modalGridApi.sizeColumnsToFit();
      }
      this.modalUser = null;
      this.modalUserNotNew = false;
      this.modalUserSignal.set(null);
      this.modalUserNotNewSignal.set(false);
    }, { injector: this.injector });
  }

  cellEditingStopped(event: CellEditingStoppedEvent<any>) {
    this.areRowsValid(this.restrictedPrograms);
    const api = event.api;
    if (event.node && api && !api.isDestroyed() && event.column) {
      refreshCellAndDeferredRowRedraw(api, event.node, event.column);
    }
  }

  refreshGrid() {
    afterNextRender(() => {
      if (this.gridApi && !this.gridApi.isDestroyed()) {
        this.gridApi.setGridOption('rowData', this.restrictedPrograms);
      }
    }, { injector: this.injector });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (this.modalGridApi && !this.modalGridApi.isDestroyed()) {
      this.modalGridApi.sizeColumnsToFit();
    }
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.sizeColumnsToFit();
    }
  }
}
